// src/components/DynamicSetupWizard/Components/VariableMapper.tsx

import React, { useEffect } from "react";
import { Row, Col, Card, Alert, Input } from "reactstrap";
import Select from "react-select";

type ModelOption = { type: "model"; value: string; label: string };
type CustomOption = { type: "custom"; value: string; label: string };
type OptionType = ModelOption | CustomOption;

export interface MappingItem {
  placeholder: string;
  mappedTo:
  | { type: "model"; modelKey: string; fieldKey?: string; fieldLabel?: string }
  | { type: "custom"; value: string }
  | null;
}

interface VariableMapperProps {
  title: string;
  description: string;
  placeholders: string[];
  mapping: MappingItem[];
  onChange: (next: MappingItem[]) => void;
  onModelSelect: (modelKey: string) => void; 
  mappingSource?: {
    type: "multiModel";
    options?: { modelKey: string; label: string }[];
  };
  properties: Array<{ modelKey: string; valuesJson: string }>;
  allowCustom: boolean;
  loading: boolean;
  loadingOptions: boolean;
  error?: string | null;
  template?: any;
}

export default function VariableMapper({
  title,
  description,
  placeholders,
  mapping,
  onChange,
  onModelSelect,
  mappingSource,
  properties,
  allowCustom,
  loading,
  loadingOptions,
  error,
  template,
}: VariableMapperProps) {
  // 1) Always seed your mapping array at the top
  useEffect(() => {
    if (mapping.length !== placeholders.length) {
      onChange(placeholders.map((ph) => ({ placeholder: ph, mappedTo: null })));
    }
  }, [placeholders, mapping, onChange]);

  // 2) Build your two-tier options exactly once
  const rawOpts = mappingSource?.options ?? [];
  const modelOpts: ModelOption[] = rawOpts.map((o) => ({
    type: "model",
    value: o.modelKey,
    label: o.label,
  }));
  const primaryOptions: OptionType[] = [
    ...modelOpts.map((m) => ({
      type: "model" as const,
      value: m.value,
      label: `${m.label} (model)`,
    })),
    ...(allowCustom
      ? [{ type: "custom" as const, value: "__custom__", label: "Custom" }]
      : []),
  ];

  // 3) Loading skeleton
  if (loading) {
    return (
      <>
        <h5 className="fw-bold">{title}</h5>
        <p className="text-secondary mb-4">{description}</p>
        {Array.from({ length: placeholders.length || 3 }).map((_, idx) => (
          <Row key={idx} className="align-items-center mb-3">
            <Col md={4}>
              <Card className="placeholder-glow p-2 bg-light text-center">
                <span className="placeholder col-6"></span>
              </Card>
            </Col>
            <Col md={8}>
              <div className="d-flex gap-2">
                <div className="placeholder w-50" style={{ height: "2.5rem" }} />
                <div className="placeholder w-50" style={{ height: "2.5rem" }} />
              </div>
            </Col>
          </Row>
        ))}
      </>
    );
  }

  return (
    <>
      <h5 className="fw-bold mb-2">{title}</h5>
      <p className="text-secondary mb-4">{description}</p>
      {error && <Alert color="danger">{error}</Alert>}

      {/*
        4) Render your template JSON, if present
      */}

      {template?.data && (
        <div
          className="border rounded p-3 mb-4 bg-light"
          dangerouslySetInnerHTML={{ __html: template.data }}
        />
      )}

      {/*
        5) Now render each placeholder row
      */}
      {mapping.map((item, idx) => {
        const { placeholder, mappedTo } = item;
        const isCustom = mappedTo?.type === "custom";

        // build the second‐level dropdown for model fields
        const fieldOpts =
        mappedTo?.type === "model"
          ? properties
              .map((p) => {
                const def = JSON.parse(p.valuesJson || "{}");
                return { value: def.key, label: def.label };
              })
          : [];

        // figure out which primary option is currently selected
        let selectedPrimary: OptionType | null = null;
        if (mappedTo) {
          if (mappedTo.type === "custom") {
            selectedPrimary = {
              type: "custom",
              value: "__custom__",
              label: "Custom",
            };
          } else {
            const lbl =
              rawOpts.find((o) => o.modelKey === mappedTo.modelKey)?.label ??
              mappedTo.modelKey;
            selectedPrimary = {
              type: "model",
              value: mappedTo.modelKey,
              label: `${lbl} (model)`,
            };
          }
        }

        return (
          <Row key={idx} className="align-items-center mb-3">
            <Col md={4}>
              <Card className="shadow-sm rounded p-2 text-center bg-light mb-0">
                {placeholder}
              </Card>
            </Col>
            <Col md={8}>
              <div className="d-flex gap-2 w-100">
                {/* primary selector */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Select<OptionType, false>
                    options={primaryOptions}
                    value={selectedPrimary}
                    onChange={(opt: { type: string; value: any; }) => {
                      const next = [...mapping];
                      if (!opt) { next[idx].mappedTo = null;
                      } else if(opt.type === "custom") {
                        next[idx].mappedTo = { type: "custom", value: "" };
                     } else {
                        next[idx].mappedTo = {
                          type: "model",
                          modelKey: opt.value,
                        };
                        onModelSelect(opt.value); 
                      }
                      onChange(next);
                    }}
                    placeholder="Model or custom…"
                    isClearable
                  />
                </div>

                {/* field or input side‐by‐side */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  {mappedTo?.type === "model" ? (
                    <Select<{ value: string; label: string }, false>
                      options={fieldOpts}
                      loading={loadingOptions}
                      value={
                        fieldOpts.find((f) => f.value === mappedTo.fieldKey) ||
                        null
                      }
                      placeholder="Select field…"
                      onChange={(opt: { value: any; label: any; }) => {
                        if (!opt) return;
                        const next = [...mapping];
                        next[idx].mappedTo = {
                          type: "model",
                          modelKey: mappedTo.modelKey,
                          fieldKey: opt.value,
                          fieldLabel: opt.label,
                        };
                        onChange(next);
                      }}
                      isClearable
                    />
                  ) : isCustom ? (
                    <Input
                      placeholder="Enter custom…"
                      value={(mappedTo as CustomOption).value}
                      onChange={(e) => {
                        const next = [...mapping];
                        next[idx].mappedTo = {
                          type: "custom",
                          value: e.target.value,
                        };
                        onChange(next);
                      }}
                    />
                  ) : null}
                </div>
              </div>
            </Col>
          </Row>
        );
      })}
    </>
  );
}
